// ============================================================================
// Users Page — Admin-only user management dashboard
// ============================================================================

import { useState, useCallback } from 'react';
import {
  UserPlus,
  Shield,
  ShieldAlert,
  ShieldCheck,
  MoreVertical,
  X,
  Eye,
  EyeOff,
  Key,
  RefreshCw,
  Copy,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import type { AppUser } from '../types';
import { USER_STATUS_INFO, ROLE_INFO } from '../types';

export function UsersPage() {
  const { users, loading, error, refetch } = useUsers();
  const { isAdmin } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState<AppUser | null>(null);
  const [actionMenuUser, setActionMenuUser] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Clear status messages after a delay
  const showSuccess = useCallback((msg: string) => {
    setActionSuccess(msg);
    setActionError(null);
    setTimeout(() => setActionSuccess(null), 4000);
  }, []);

  const showError = useCallback((msg: string) => {
    setActionError(msg);
    setActionSuccess(null);
    setTimeout(() => setActionError(null), 6000);
  }, []);

  // ── Actions ──────────────────────────────────────────────────────

  const handleToggleEnabled = async (user: AppUser) => {
    setActionLoading(user.email);
    setActionMenuUser(null);
    try {
      const action = user.enabled ? 'disable' : 'enable';
      await api.updateUser(user.email, { action });
      showSuccess(`User ${user.email} ${action}d successfully`);
      await refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async (user: AppUser, newRole: 'admin' | 'editor') => {
    setActionLoading(user.email);
    setActionMenuUser(null);
    try {
      await api.updateUser(user.email, { action: 'changeRole', role: newRole });
      showSuccess(`${user.email} role changed to ${newRole}`);
      await refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to change role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (user: AppUser) => {
    setActionMenuUser(null);
    setShowPasswordModal(user);
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <ShieldAlert className="mb-4 h-12 w-12" />
        <h2 className="text-lg font-semibold">Access Denied</h2>
        <p className="mt-1 text-sm">You need admin privileges to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage team access and permissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refetch}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Invite User
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {actionSuccess && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
          <Check className="h-4 w-4 flex-shrink-0" />
          {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {actionError}
        </div>
      )}

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-green border-t-transparent" />
          <span className="ml-3 text-sm text-gray-500">Loading users...</span>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-6 text-center">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={refetch} className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 underline">
            Try again
          </button>
        </div>
      )}

      {/* User List */}
      {!loading && !error && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => {
                const statusInfo = user.enabled
                  ? (USER_STATUS_INFO[user.status] ?? USER_STATUS_INFO.UNKNOWN!)
                  : USER_STATUS_INFO.DISABLED!;
                const roleInfo = ROLE_INFO[user.role] || ROLE_INFO.none;
                const isLoadingUser = actionLoading === user.email;

                return (
                  <tr key={user.userId || user.email} className={`transition-colors ${isLoadingUser ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                    {/* User info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-green-50 text-brand-green text-sm font-bold uppercase">
                          {(user.fullName || user.email).charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.fullName || '—'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${roleInfo.bgColor} ${roleInfo.color}`}>
                        {user.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                        {roleInfo.label}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy') : '—'}
                    </td>

                    {/* Actions dropdown */}
                    <td className="px-6 py-4 text-right relative">
                      <button
                        onClick={() => setActionMenuUser(actionMenuUser === user.email ? null : user.email)}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        disabled={isLoadingUser}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                      {actionMenuUser === user.email && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setActionMenuUser(null)} />
                          <div className="absolute right-6 top-12 z-20 w-52 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                            {/* Toggle enable/disable */}
                            <button
                              onClick={() => handleToggleEnabled(user)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              {user.enabled ? (
                                <>
                                  <EyeOff className="h-4 w-4 text-red-500" />
                                  Suspend Access
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 text-green-500" />
                                  Re-enable Access
                                </>
                              )}
                            </button>

                            {/* Change role */}
                            <button
                              onClick={() => handleChangeRole(user, user.role === 'admin' ? 'editor' : 'admin')}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Shield className="h-4 w-4 text-purple-500" />
                              {user.role === 'admin' ? 'Change to Editor' : 'Change to Admin'}
                            </button>

                            <div className="my-1 border-t border-gray-100" />

                            {/* Reset password */}
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Key className="h-4 w-4 text-amber-500" />
                              Reset Password
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                    No users found. Invite your first team member!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={(msg) => {
            setShowInviteModal(false);
            showSuccess(msg);
            refetch();
          }}
          onError={showError}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <PasswordResetModal
          user={showPasswordModal}
          onClose={() => setShowPasswordModal(null)}
          onSuccess={(msg) => {
            setShowPasswordModal(null);
            showSuccess(msg);
          }}
          onError={showError}
        />
      )}
    </div>
  );
}

// ── Invite User Modal ──────────────────────────────────────────────────────

function InviteUserModal({
  onClose,
  onSuccess,
  onError,
}: {
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'editor'>('editor');
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState<{ email: string; tempPassword: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !fullName) return;

    setLoading(true);
    try {
      const result = await api.createUser({ email, fullName, role });
      setCreatedUser({ email: result.email, tempPassword: result.tempPassword });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to create user');
      setLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (createdUser) {
      navigator.clipboard.writeText(createdUser.tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDone = () => {
    onSuccess(`User ${createdUser?.email || email} invited successfully`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {createdUser ? 'User Created' : 'Invite New User'}
          </h2>
          <button onClick={createdUser ? handleDone : onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {createdUser ? (
          /* Show temp password after creation */
          <div className="space-y-4">
            <div className="rounded-lg bg-green-50 border border-green-200 p-4">
              <p className="text-sm font-medium text-green-800">
                ✅ User created successfully!
              </p>
              <p className="mt-1 text-xs text-green-700">
                Share these credentials with the user. They'll be prompted to set a new password on first login.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="label">Email</label>
                <p className="text-sm font-medium text-gray-900">{createdUser.email}</p>
              </div>
              <div>
                <label className="label">Temporary Password</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-mono text-gray-800">
                    {createdUser.tempPassword}
                  </code>
                  <button
                    onClick={handleCopyPassword}
                    className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                    title="Copy password"
                  >
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <button onClick={handleDone} className="btn-primary w-full py-2.5">
              Done
            </button>
          </div>
        ) : (
          /* Invite form */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="invite-email" className="label">Email</label>
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
                placeholder="blake@creamcitymortgage.com"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="invite-name" className="label">Full Name</label>
              <input
                id="invite-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input mt-1"
                placeholder="Blake Early"
                required
              />
            </div>

            <div>
              <label className="label">Role</label>
              <div className="mt-1 flex gap-3">
                <button
                  type="button"
                  onClick={() => setRole('editor')}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors ${
                    role === 'editor'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Shield className="mx-auto mb-1 h-5 w-5" />
                  Editor
                  <p className="mt-0.5 text-[10px] font-normal opacity-70">Upload, edit drafts</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={`flex-1 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors ${
                    role === 'admin'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <ShieldCheck className="mx-auto mb-1 h-5 w-5" />
                  Admin
                  <p className="mt-0.5 text-[10px] font-normal opacity-70">Full access</p>
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1 py-2.5">
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Password Reset Modal ────────────────────────────────────────────────────

function PasswordResetModal({
  user,
  onClose,
  onSuccess,
  onError,
}: {
  user: AppUser;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<'reset' | 'setPassword'>('reset');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'reset') {
        const result = await api.resetUserPassword(user.email, { action: 'reset' });
        onSuccess(result.message);
      } else {
        if (!newPassword || newPassword.length < 8) {
          onError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        const result = await api.resetUserPassword(user.email, {
          action: 'setPassword',
          newPassword,
          permanent: true,
        });
        onSuccess(result.message);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-4 text-sm text-gray-500">
          Reset password for <strong>{user.email}</strong>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode selector */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('reset')}
              className={`flex-1 rounded-lg border-2 px-3 py-2 text-xs font-medium transition-colors ${
                mode === 'reset'
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Force Reset
              <p className="mt-0.5 text-[10px] font-normal opacity-70">User sets new password at login</p>
            </button>
            <button
              type="button"
              onClick={() => setMode('setPassword')}
              className={`flex-1 rounded-lg border-2 px-3 py-2 text-xs font-medium transition-colors ${
                mode === 'setPassword'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Set Password
              <p className="mt-0.5 text-[10px] font-normal opacity-70">Set a specific new password</p>
            </button>
          </div>

          {mode === 'setPassword' && (
            <div>
              <label htmlFor="new-password" className="label">New Password</label>
              <div className="relative mt-1">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          {mode === 'reset' && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs text-amber-800">
                This will invalidate the user's current password. They'll be prompted to set a new password on their next login.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 py-2.5">
              {loading ? 'Processing...' : mode === 'reset' ? 'Reset Password' : 'Set Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
